import React from 'react'
import { Button, CircularProgress, Box, Typography, styled } from '@mui/material'

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'version' && prop !== 'labelM',
})(({ version, labelM }) => ({
  borderRadius: 6,

  ...(version === 'delete' && {
    backgroundColor: '#dd422a',
    '&:hover': {
      backgroundColor: '#c0392b',
    },
  }),

  textTransform: 'none',
  fontFamily: 'FSEmeric-Medium',
  fontSize: 14,

  ...((labelM === 'lg' && { padding: '4px 72px' }) ||
    (labelM === 'md' && { padding: '4px 50px' }) ||
    (labelM === 'sm' && { padding: '4px 35px' }) ||
    {}),
}))

const StyledExtraText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'truncate',
})(({ truncate }) => ({
  whiteSpace: 'pre-line',

  ...(truncate && {
    display: 'inline-block',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
}))

/**
 * @typedef {object} ThemeButtonProps
 * @property {'contained' | 'outlined' | 'text'} [variant='contained'] - Variante visual del botón.
 * @property {'primary' | 'secondary' | 'error' | 'success' | 'warning' | 'info'} [color='primary'] - Color del botón.
 * @property {'small' | 'medium' | 'large'} [size='large'] - Tamaño del botón.
 * @property {'lg' | 'md' | 'sm'} [labelM='sm'] - Tamaño del padding horizontal del botón.
 * @property {React.ReactNode} children - Contenido del botón (texto, ícono, etc.).
 * @property {boolean} [loading=false] - Si es true, muestra un spinner de carga.
 * @property {boolean} [truncate=false] - Si es true, trunca el texto extra con ellipsis.
 * @property {boolean} [disabled] - Si es true, deshabilita el botón.
 * @property {'delete'} [version] - Versión especial del botón (ej: 'delete' para color rojo).
 * @property {string} [extra] - Texto adicional mostrado debajo del botón (caption).
 */

/**
 * Botón temático con soporte para estados de carga, variantes y texto adicional.
 *
 * Botón personalizado con estilos del tema, opciones de tamaño, color, variante, estado de carga,
 * y texto adicional (extra) debajo del botón. Incluye variante especial 'delete' con color rojo.
 *
 * @component
 * @example
 * <ThemeButton
 *   variant="contained"
 *   color="primary"
 *   size="large"
 *   labelM="md"
 *   loading={false}
 *   onClick={() => console.log('Clicked')}
 *   extra="Texto adicional debajo del botón"
 *   truncate={true}
 * >
 *   Guardar
 * </ThemeButton>
 *
 * @param {ThemeButtonProps} props - Las props del componente ThemeButton.
 */
export const ThemeButton = ({
  variant = 'contained',
  color = 'primary',
  size = 'large',
  labelM = 'sm',
  children,
  loading = false,
  truncate = false,
  ...props
}) => (
  <Box display="block">
    <StyledButton
      {...props}
      size={size}
      variant={variant}
      color={color}
      disabled={props.disabled || loading}
      disableElevation
      version={props.version}
      labelM={labelM}
    >
      {loading ? (
        <CircularProgress size={size === 'large' ? 30 : 25} color={'primary'} />
      ) : (
        <Box display="flex" alignItems="center">
          {children}
        </Box>
      )}
    </StyledButton>
    {props.extra && (
      <Box>
        <StyledExtraText variant="caption" color="textSecondary" truncate={truncate}>
          {props.extra}
        </StyledExtraText>
      </Box>
    )}
  </Box>
)
